/// <amd-module name="DistributedTask/Common/Library/AgentQueueSelector" />
import * as React from "react";

import { TaskAgentQueue } from "DistributedTask/Client/Contracts/TaskAgent";
import { AgentSpecificationDropdown } from "DistributedTask/Common/Library/AgentSpecificationDropdown";
import * as Resources from "DistributedTask/Common/Library/Resources";
import { IAgentSpecification } from "DistributedTask/Common/Library/Sources/AgentSpecificationService";

import { PickListDropdown, IPickListSelection, IPickListItem } from "VSSUI/PickList";
import { Spinner } from "VSSUI/Spinner";

export interface IAgentQueueSelectorProps {
    label: string;
    autoFocus?: boolean;
    agentQueues: TaskAgentQueue[];
    selectedQueue: TaskAgentQueue;
    onAgentChanged?(agentQueue: TaskAgentQueue): void;
    agentSpecifications: IAgentSpecification[];
    selectedAgentSpecification?: IAgentSpecification;
    onAgentSpecificationSelected(agentSpecification: IAgentSpecification): void;
    agentSpecificationErrorMessage: string;
    isLoadingAgentSpecifications?: boolean;
}

enum AgentQueueGroup {
    Hosted = "1",
    Private = "2"
}

export class AgentQueueSelector extends React.PureComponent<IAgentQueueSelectorProps> {
    private _agentQueuePickListRef: React.RefObject<PickListDropdown<TaskAgentQueue>> = React.createRef<PickListDropdown<TaskAgentQueue>>();

    public render(): JSX.Element {
        // provide format string that will combine label + inital value
        const ariaLabelFormat = `${this.props.label} {0}`;
        return <div className="flex-column label">
                    <label className={"label"}>{this.props.label}</label>
                    <PickListDropdown<TaskAgentQueue>
                        ariaLabelFormat={ariaLabelFormat}
                        className="agent-pool-container"
                        selectOnFocus={true}
                        selectedItems={[this.props.selectedQueue || {}]}
                        getPickListItems={this._getAgentQueues}
                        onSelectionChanged={this._onAgentChanged}
                        getListItem={this._getListItem}
                        ref={this._agentQueuePickListRef}
                        groups={[
                            {
                                name: Resources.QueueOptionHeaderHosted,
                                key: AgentQueueGroup.Hosted
                            },
                            {
                                name: Resources.QueueOptionHeaderPrivate,
                                key: AgentQueueGroup.Private
                            }
                        ]}
                    />
                    {this.props.isLoadingAgentSpecifications ?
                        <Spinner className="queue-build-spinner" /> :
                        <AgentSpecificationDropdown
                            agentQueue={this.props.selectedQueue}
                            agentSpecifications={this.props.agentSpecifications}
                            selectedAgentSpecification={this.props.selectedAgentSpecification}
                            errorMessage={this.props.agentSpecificationErrorMessage}
                            onChanged={this._handleAgentSpecificationChanged} />
                    }
                </div>;
    }

    public componentDidMount() {
        if (this.props.autoFocus && this._agentQueuePickListRef && this._agentQueuePickListRef.current)
        {
            this._agentQueuePickListRef.current.focus();
        }
    }

    private _getListItem = (item: TaskAgentQueue): IPickListItem => {
        let groupKey = AgentQueueGroup.Private;

        if (item.pool && item.pool.isHosted) {
            groupKey = AgentQueueGroup.Hosted;
        }

        return {
            name: item.name,
            key: item.id.toString(),
            groupKey,
        } as IPickListItem;
    }

    private _onAgentChanged = (selection: IPickListSelection) => {
        const selectedItems = selection.selectedItems;
        let queue: TaskAgentQueue = {} as TaskAgentQueue;

        if (selectedItems && selectedItems.length > 0) {
            this.props.agentQueues.some(agentQueue => {
                if (agentQueue.id === selectedItems[0].id)
                {
                    queue = agentQueue;
                    return true;
                }
                return false;
            });
        }

        if (this.props.onAgentChanged) {
            this.props.onAgentChanged(queue);
        }
    }

    private _getAgentQueues = (): TaskAgentQueue[] => {
        return this.props.agentQueues;
    }

    private _handleAgentSpecificationChanged = (agentSpecification: IAgentSpecification) => {
        this.props.onAgentSpecificationSelected(agentSpecification);
    } 
}
